/**
 * Pure pointer-sample → drafting-aid-constrained snap resolver.
 *
 * Extracted from `stageInteractions.resolvePointerSnapAt` (the file sits at its
 * 2000-line cap — same split rationale as `pointerSnapStep` / `draftAidConstraint`
 * / `orthoDraftLock`). Given a resolved raw cursor + the two active input spaces
 * (gated `space` + ungated `draftSpace`), this owns:
 *
 *   1. the `draftPastEdge` gate (#234 / #299 @codex P2) — mid-draft, cursor
 *      OUTSIDE the active VP rect, the gated OSNAP step is skipped and replaced
 *      with an empty result (AutoCAD floating MSPACE engages NO paper OSNAP past
 *      the frame);
 *   2. the raw OSNAP resolve via `pointerSnapStep`;
 *   3. the ORTHO/POLAR aid lock via `resolveDraftAidConstraint`;
 *   4. the #481 ORTHO-locked OSNAP re-resolve (below).
 *
 * THREE-free (imports only types from `three` transitively via `ActiveInputSpace`)
 * and side-effect-free — the caller applies overlay/cache/hover side effects.
 */

import {
  pointerSnapStep,
  type OsnapBindings,
  type PointerSnapStepResult,
} from "./pointerSnapStep";
import {
  resolveDraftAidConstraint,
  type DraftAidResult,
} from "./draftAidConstraint";
import type { ActiveInputSpace } from "../viewports/activeInputSpace";
import {
  pointInViewport,
  type PaperOrigin,
} from "../viewports/viewportTransform2d";
import type { SpatialIndex, Vec2 } from "../types";

/** Inputs for {@link resolveAidedPointerSnap}. */
export interface AidedPointerSnapArgs {
  /** Paper-world cursor (the `clientToWorld` result against the paper camera). */
  readonly rawWorld: Vec2;
  /** Cursor client position (passed through to `pointerSnapStep`). */
  readonly cursorClient: { x: number; y: number };
  /** GATED active input space (`ignoreCursorRect:false`) — feeds OSNAP. */
  readonly space: ActiveInputSpace;
  /** UNGATED draft-point space (`ignoreCursorRect:true`); === `space` when not drafting. */
  readonly draftSpace: ActiveInputSpace;
  /** Whether a draft session is in progress (`draftActiveRef`). */
  readonly drafting: boolean;
  /** ORTHO/POLAR draft anchor ($LASTPOINT), or null (first point / no anchor). */
  readonly anchor: { x: number; y: number } | null;
  /**
   * N6 (POLAR-RELATIVE-BASIS-WIRE) — direction (degrees) of the last segment
   * drawn (`draftSharedHelpers.polarReferenceAngleForDraftSession`), or null
   * (first segment / no draft). Forwarded to `resolveDraftAidConstraint` for
   * POLARMODE's relative-basis table rotation; ignored under absolute basis.
   */
  readonly referenceAngleDeg?: number | null;
  /**
   * Active-VP paper origin for the POLAR guide clip and the VP re-gates, in
   * the renderer's OFFSET-world frame (same frame as `rawWorld`), or null.
   */
  readonly paperOrigin: PaperOrigin | null;
  /** Live OSNAP bindings (`osnapBindingsRef.current`); null disables snap. */
  readonly osnap: OsnapBindings | null;
  /** Live spatial index (`spatialIndexRef.current`); null disables both paths. */
  readonly spatialIndex: SpatialIndex | null;
  /** Hover tolerance in CSS pixels (`DRAG_THRESHOLD_PX`). */
  readonly dragThresholdPx: number;
  /** Whether the caller has an `onCanvasHover` binding (skips hover work if not). */
  readonly wantHover: boolean;
  /**
   * Codex PR #784 round 4 F2 — global off / frozen layer set, forwarded
   * verbatim to `pointerSnapStep` so the hover hit-test applies the SAME
   * INSERT child-geometry filter as the click path. See that arg's doc.
   */
  readonly hiddenLayers?: ReadonlySet<string> | null;
}

/** Result of {@link resolveAidedPointerSnap}. */
export interface AidedPointerSnapResult {
  readonly drafting: boolean;
  /** The (possibly re-resolved) snap bundle — cache + overlay + hover source. */
  readonly snap: PointerSnapStepResult;
  readonly emitSpaceId: string;
  /** ORTHO/POLAR-locked (or OSNAP-overridden) emit — the committed draft point. */
  readonly constrainedWorld: DraftAidResult["world"];
  readonly polarGuide: DraftAidResult["polarGuide"];
  readonly polarTip: DraftAidResult["polarTip"];
  /** OTRACK-SUBSYSTEM — engaged alignment-path guide lines (0, 1, or 2 entries). */
  readonly otrackGuides: DraftAidResult["otrackGuides"];
  /** OTRACK-SUBSYSTEM — tracking tooltip, or null when idle. */
  readonly otrackTip: DraftAidResult["otrackTip"];
  readonly viewportPresent: boolean;
}

/**
 * Resolve the final aid-constrained draft point + snap for one pointer sample.
 * Pure — returns data only; the caller owns overlay/cache/hover side effects.
 */
export function resolveAidedPointerSnap(
  args: AidedPointerSnapArgs,
): AidedPointerSnapResult {
  const {
    rawWorld,
    cursorClient,
    space,
    draftSpace,
    drafting,
    anchor,
    referenceAngleDeg,
    paperOrigin,
    osnap,
    spatialIndex,
    dragThresholdPx,
    wantHover,
  } = args;

  // Draft-past-edge divergence: drafting with the cursor OUTSIDE the VP rect —
  // the ungated resolve redirected into the VP while the gated one fell through
  // to identity. #299 @codex P2 ×2 — the gated `pointerSnapStep` is SKIPPED and
  // replaced with an empty result (AutoCAD floating MSPACE engages NO paper
  // OSNAP past the frame): marker/tooltip clear, no "model" hover, cycling clears.
  const draftPastEdge =
    drafting && draftSpace.viewport !== null && space.viewport === null;
  const snap: PointerSnapStepResult = draftPastEdge
    ? {
        world: space.toSpace(rawWorld),
        magnetized: false,
        snapHit: null,
        snapTarget: null,
        snapTip: null,
        hoverHitId: null,
        rawHoverHitId: null,
        stackedCandidates: [],
      }
    : pointerSnapStep({
        rawWorld,
        cursorClient,
        space,
        osnap,
        spatialIndex,
        dragThresholdPx,
        wantHover,
        hiddenLayers: args.hiddenLayers ?? null,
      });
  const emitWorld = draftPastEdge ? draftSpace.toSpace(rawWorld) : snap.world;
  const emitSpaceId = draftPastEdge ? draftSpace.spaceId : space.spaceId;
  // OTRACK-SUBSYSTEM — OSNAP aperture converted to WORLD units in the active
  // emit space, mirroring `resolveObjectSnap`'s own `aperturePx /
  // pixelsPerWorld` conversion. `osnap == null` (no bindings yet) disables
  // OTRACK engagement for this sample, same as it disables OSNAP itself.
  const otrackApertureWorld =
    osnap != null
      ? osnap.aperturePx / Math.max((draftPastEdge ? draftSpace : space).pixelsPerWorld, 1e-12)
      : 0;
  const aid = resolveDraftAidConstraint(
    emitWorld,
    anchor,
    snap.magnetized,
    (draftPastEdge ? draftSpace : space).fromSpace, // reproject guide→paper
    (draftPastEdge ? draftSpace : space).viewport, // #492 P2: + VP-clip guide
    paperOrigin,
    referenceAngleDeg, // N6 — POLAR relative-basis reference angle.
    otrackApertureWorld,
    // OTRACK-SUBSYSTEM (local Codex review round 5 P2) — filter acquired
    // points to THIS emit space before alignment (see
    // `otrackLockedCursor`'s `currentSpaceId` doc).
    emitSpaceId,
    // OTRACK-SUBSYSTEM — the acquired-point store is DOCUMENT-framed (one
    // frame for both its writers); this is the branch-aware conversion into
    // the emit space the alignment math and `fromSpace` above both run in.
    (draftPastEdge ? draftSpace : space).fromDocument,
  );

  // #481 (@codex P2 "Resolve OSNAP from the ORTHO-constrained cursor") — the
  // OSNAP resolve ABOVE ran against the RAW physical cursor. When ORTHO then
  // locked the emit onto the 0/90° axis, an object-snap target near the LOCKED
  // crosshair but OUTSIDE the raw-cursor aperture was never evaluated, so the
  // click committed the diagonal-projected point instead of snapping. AutoCAD
  // evaluates OSNAP against the constrained cursor and lets it OVERRIDE ORTHO.
  // Re-resolve OSNAP at the locked position and, if it magnetizes, commit the
  // snapped point (and move the marker there). Guards keep every other path
  // byte-identical:
  //   - `drafting` + not `draftPastEdge` — past the VP edge the snap step is
  //     intentionally empty (#299), so there is nothing to re-resolve;
  //   - `!snap.magnetized` — the raw cursor already snapped (ortho stood down);
  //   - `!aid.polarEngaged && !aid.otrackEngaged && aid.world != emitWorld` —
  //     the aid that moved the emit was ORTHO, not POLAR or OTRACK, i.e.
  //     ORTHO actually engaged. Non-ortho paths leave `aid.world ===
  //     emitWorld` (same object), so this never fires and the resolve is
  //     skipped. `polarEngaged` (NOT `polarGuide == null`) is the
  //     discriminator (local Codex review round 2 P1): TRACKPATH
  //     suppression / an off-frame clip can null `polarGuide` while polar is
  //     genuinely engaged, and using the display field here would wrongly
  //     route a TRACKPATH-suppressed polar lock through the ORTHO-only OSNAP
  //     re-resolve below, letting a nearby OSNAP target silently override the
  //     polar-locked point the way it is only supposed to override ORTHO.
  let constrainedWorld: { x: number; y: number } = aid.world;
  let resolvedSnap = snap;
  // OTRACK-SUBSYSTEM — `!aid.otrackEngaged` excludes an OTRACK-locked sample
  // from this ORTHO-only re-resolve too (same reasoning as the polar
  // exclusion above). OTRACK deliberately carries no analogous re-resolve
  // pass of its own (documented scope boundary — design note §2): OSNAP
  // magnet already overrides OTRACK UPSTREAM, at `otrackLockedCursor`'s own
  // `snapMagnetized` gate, exactly like it overrides ORTHO/POLAR.
  const orthoEngaged =
    !aid.polarEngaged &&
    !aid.otrackEngaged &&
    (aid.world.x !== emitWorld.x || aid.world.y !== emitWorld.y);
  // Map the locked emit (in `space.spaceId`) back to paper-world ONCE: it both
  // gates the re-resolve against the VP frame AND feeds `pointerSnapStep` so the
  // re-resolve runs the identical filtered pipeline at the constrained cursor
  // (`space.toSpace(fromSpace(x)) === x` round-trips).
  const lockedPaper = space.fromSpace(aid.world);
  // #511 @codex P2 — re-gate the ORTHO-locked re-resolve against the VP frame.
  // Inside a floating viewport ORTHO can project the emit to a PAPER position
  // OUTSIDE the VP rect (a twisted VP, or drafting back from a point placed past
  // the frame). Re-resolving model OSNAP there would paint/commit a snap to
  // geometry invisible past the frame — exactly the leak the `draftPastEdge` gate
  // blocks for the raw cursor (AutoCAD floating MSPACE engages NO paper OSNAP past
  // the frame). When redirected into a VP, skip the re-resolve unless the locked
  // paper point is inside that VP rect; outside a VP (`space.viewport === null`,
  // or no `paperOrigin`) there is no frame to gate against, so it stays allowed.
  const lockedInsideVp =
    space.viewport === null ||
    paperOrigin === null ||
    pointInViewport(lockedPaper, space.viewport, paperOrigin);
  if (
    drafting &&
    !draftPastEdge &&
    !snap.magnetized &&
    orthoEngaged &&
    lockedInsideVp
  ) {
    const lockedSnap = pointerSnapStep({
      rawWorld: lockedPaper,
      cursorClient,
      space,
      osnap,
      spatialIndex,
      dragThresholdPx,
      // Hover / cycling follow the PHYSICAL cursor, not the ortho lock — keep
      // the raw-cursor `snap`'s hoverHitId + stackedCandidates.
      wantHover: false,
    });
    // #511 @codex P2 (follow-up) — `lockedInsideVp` only gates the LOCKED
    // CURSOR position against the VP frame. Inside a floating viewport the
    // OSNAP aperture can still magnetize to a model endpoint whose OWN
    // paper-projected position lies just past the frame while the cursor
    // itself sits inside it (edge-near drafting). Re-gate the ACTUAL hit
    // position (`snapHit.world`, mapped to paper the same way `lockedPaper`
    // was) before letting it override ORTHO or paint a marker — geometry
    // invisible past the frame must neither commit NOR show a marker/tooltip
    // (AutoCAD floating MSPACE parity, same principle as `draftPastEdge`).
    const hitInsideVp =
      lockedSnap.snapHit === null ||
      space.viewport === null ||
      paperOrigin === null ||
      pointInViewport(space.fromSpace(lockedSnap.snapHit.world), space.viewport, paperOrigin);
    if (lockedSnap.magnetized && hitInsideVp) {
      // Object snap overrides ORTHO: commit the snapped point and repaint the
      // marker/tooltip at it (both already in the correct spaces).
      constrainedWorld = lockedSnap.world;
      resolvedSnap = {
        ...snap,
        world: lockedSnap.world,
        magnetized: true,
        snapHit: lockedSnap.snapHit,
        snapTarget: lockedSnap.snapTarget,
        snapTip: lockedSnap.snapTip,
      };
    } else if (
      !lockedSnap.magnetized &&
      hitInsideVp &&
      (lockedSnap.snapTarget !== null || lockedSnap.snapTip !== null)
    ) {
      // #511 @codex P3 — marker-only OSNAP (AUTOSNAP marker/tooltip on, MAGNET
      // bit OFF) at the ORTHO-locked crosshair. The re-resolve found a hit whose
      // POSITION is not committed (magnet off ⇒ the emit stays ortho-locked), but
      // the overlay must still reflect the locked-position marker/tooltip — an
      // endpoint inside the ORTHO-locked aperture yet outside the raw-cursor
      // aperture is otherwise invisible even though marker-only OSNAP is
      // supported. Keep `constrainedWorld`/`world`/`magnetized` ortho-locked;
      // surface only the locked hit's marker/tooltip (and its owner `snapHit`).
      resolvedSnap = {
        ...snap,
        snapHit: lockedSnap.snapHit,
        snapTarget: lockedSnap.snapTarget,
        snapTip: lockedSnap.snapTip,
      };
    }
  }

  return {
    drafting,
    snap: resolvedSnap,
    emitSpaceId,
    constrainedWorld,
    polarGuide: aid.polarGuide,
    polarTip: aid.polarTip,
    otrackGuides: aid.otrackGuides,
    otrackTip: aid.otrackTip,
    viewportPresent: !!draftSpace.viewport,
  };
}
